import axios from 'axios';
import authService from './authService';

const API_BASE_URL = process.env.REACT_APP_API_URL || process.env.REACT_APP_API_URL || 'http://localhost:8000';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' }
});

// Endpoints publics (pas besoin de token)
// Format: { url: string, methods?: string[] } - si methods est vide, toutes les méthodes sont publiques
const PUBLIC_ENDPOINTS = [
  { url: '/api/auth/login/', methods: [] },
  { url: '/api/auth/register/', methods: [] },
  { url: '/api/iris/', methods: [] },
  { url: '/api/services/', methods: ['GET'] },  // GET public, POST/PUT/DELETE nécessitent auth
  { url: '/api/packs/', methods: ['GET'] },     // GET public, POST/PUT/DELETE nécessitent auth
  { url: '/api/promotions/', methods: ['GET'] }, // GET public, POST/PUT/DELETE nécessitent auth
  { url: '/api/discussions/all/', methods: ['GET'] },
  { url: '/api/company/profile/', methods: ['GET'] }, // Profile public en lecture
  { url: '/api/promo-wheel/data/', methods: ['GET'] }, // Données de la roue publiques en lecture
];

function isPublicEndpoint(url?: string, method?: string): boolean {
  if (!url) return false;
  
  const httpMethod = method?.toUpperCase() || 'GET';
  
  // Vérifier les endpoints spécifiques avec leurs méthodes
  for (const endpoint of PUBLIC_ENDPOINTS) {
    // Vérification exacte pour éviter les faux positifs
    // Ne pas utiliser startsWith pour éviter que /api/promotions/user-codes/ soit considéré comme /api/promotions/
    const isMatchingUrl = 
      url === endpoint.url || 
      url.startsWith(`${endpoint.url}?`);
    
    if (isMatchingUrl) {
      // Si methods est vide, toutes les méthodes sont publiques
      if (endpoint.methods.length === 0) return true;
      // Sinon, vérifier si la méthode est dans la liste
      return endpoint.methods.includes(httpMethod);
    }
  }
  
  // Permettre l'accès aux packs individuels (GET seulement)
  if (url.match(/^\/api\/packs\/\d+\/$/) && httpMethod === 'GET') return true;
  
  return false;
}

// Request interceptor : ajouter le token
api.interceptors.request.use(
  async (config) => {
    // Ne pas ajouter de token pour les endpoints publics
    if (isPublicEndpoint(config.url, config.method)) {
      console.log('🌐 Endpoint public:', config.method?.toUpperCase(), config.url, '- Pas de token requis');
      return config;
    }

    const token = authService.getAccessToken();
    console.log('🔑 Intercepteur API:', {
      url: config.url,
      method: config.method?.toUpperCase(),
      token: token ? `Présent (${token.substring(0, 20)}...)` : 'ABSENT ❌',
      hasAuthHeader: !!config.headers.Authorization
    });
    
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      console.log('✅ Token ajouté au header Authorization');
    } else {
      console.warn('⚠️ Aucun token disponible pour', config.url);
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor : gérer les 401
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Si 401 et pas déjà retry
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      // Ne pas essayer de refresh pour les endpoints publics
      if (isPublicEndpoint(originalRequest.url, originalRequest.method)) {
        return Promise.reject(error);
      }

      // Essayer de rafraîchir le token
      const refreshed = await authService.refreshToken();
      
      if (refreshed) {
        // Retry la requête avec le nouveau token
        const newToken = authService.getAccessToken();
        if (newToken) {
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
        }
        return api(originalRequest);
      } else {
        // Refresh échoué → logout
        authService.logout();
        return Promise.reject(error);
      }
    }

    return Promise.reject(error);
  }
);

export default api;
